/**
 * 화면 표기 포맷. 금액·수량·날짜를 만드는 곳은 여기 **하나뿐**이다
 * (`modules/quote/ui/format` 은 이 파일의 re-export 다).
 *
 * 시각은 전부 **Asia/Seoul** 로 찍는다. 서버 컴포넌트는 배포 서버의 TZ 를,
 * 클라이언트는 브라우저 TZ 를 따르므로 고정하지 않으면 같은 이벤트가 화면마다
 * 9시간씩 다르게 보인다.
 *
 * 서버 컴포넌트·클라이언트 컴포넌트 양쪽에서 같이 쓴다(브라우저 API 를 쓰지 않는다).
 */

import dayjs from "dayjs";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";

dayjs.extend(utc);
dayjs.extend(timezone);

/** 이 제품의 시간대. 국내 시공사만 쓰므로 조직 설정으로 빼지 않는다. */
export const APP_TZ = "Asia/Seoul";

/** `2026-09-15` 같은 날짜 문자열. 시각이 없으니 시간대를 옮기면 날짜가 밀린다. */
const PLAIN_DATE = /^\d{4}-\d{2}-\d{2}$/;

/** 시각으로 읽은 뒤 KST 로 옮긴다. 날짜만 있는 값은 그대로 본다. */
const kst = (value: string): dayjs.Dayjs =>
  PLAIN_DATE.test(value) ? dayjs(value) : dayjs(value).tz(APP_TZ);

/** 값이 없을 때 쓰는 자리표시. 표의 빈 칸이 사라지지 않게 문자 하나는 남긴다. */
export const EMPTY_MARK = "—";

/** `1234000` → `1,234,000`. 소수는 버리지 않고 그대로 둔다. */
export const formatNumber = (value: number): string =>
  value.toLocaleString("ko-KR");

/** 금액 숫자만. 원 단위 아래는 없으므로 반올림해서 보여 준다. */
export const formatAmount = (value: number): string =>
  Math.round(value).toLocaleString("ko-KR");

/** 수량은 소수가 올 수 있다. 1.5 는 1.5, 2 는 2 로. */
export const formatQty = (value: number): string => String(value);

/** 0.04 → "4%". 소수점은 필요할 때만. */
export const formatRate = (rate: number): string => {
  const pct = rate * 100;
  return `${Number.isInteger(pct) ? pct : Number(pct.toFixed(2))}%`;
};

// 걸린 시간(분) → 「45분」·「1시간 5분」·「2일 3시간」. 음수·NaN 은 자리표시.
export const formatElapsed = (minutes: number | null | undefined): string => {
  if (typeof minutes !== "number" || !Number.isFinite(minutes) || minutes < 0) return EMPTY_MARK;
  const m = Math.floor(minutes);
  if (m < 1) return "1분 미만";
  if (m < 60) return `${m}분`;
  if (m < 1440) {
    const rest = m % 60;
    return rest ? `${Math.floor(m / 60)}시간 ${rest}분` : `${Math.floor(m / 60)}시간`;
  }
  const hours = Math.floor((m % 1440) / 60);
  return hours ? `${Math.floor(m / 1440)}일 ${hours}시간` : `${Math.floor(m / 1440)}일`;
};

/** 입력 문자열 → 0 이상 정수. 콤마·공백·기호를 털어 낸다. */
export const parseMoney = (text: string): number => {
  const digits = text.replace(/[^\d]/g, "");
  if (digits.length === 0) return 0;
  const n = Number(digits);
  return Number.isFinite(n) ? n : 0;
};

/** 입력 문자열 → 0 이상 수량(소수 허용). */
export const parseQty = (text: string): number => {
  const cleaned = text.replace(/[^\d.]/g, "");
  const n = Number(cleaned);
  return Number.isFinite(n) && n >= 0 ? n : 0;
};

/** `1234000` → `1,234,000원`. null 이면 자리표시. */
export const formatWon = (
  value: number | null | undefined,
  fallback: string = EMPTY_MARK
): string =>
  typeof value === "number" && Number.isFinite(value)
    ? `${formatNumber(value)}원`
    : fallback;

/** 입력창에서 온 `1,200만` 같은 문자열에서 숫자만 뽑는다. 없으면 null. */
export const parseWon = (input: string): number | null => {
  const digits = input.replace(/[^\d]/g, "");
  if (!digits) return null;
  const value = Number(digits);
  return Number.isFinite(value) ? value : null;
};

/** ISO·`YYYY-MM-DD` 둘 다 받는다. 못 읽으면 자리표시. */
export const formatDate = (
  value: string | null | undefined,
  fallback: string = EMPTY_MARK
): string => {
  if (!value) return fallback;
  const d = kst(value);
  return d.isValid() ? d.format("YYYY-MM-DD") : fallback;
};

/** 2026-09-15 14:03 (KST). 이력은 분까지 보여야 「누가 먼저였나」 가 읽힌다. */
export const formatDateTime = (
  value: string | null | undefined,
  fallback: string = EMPTY_MARK
): string => {
  if (!value) return fallback;
  const d = kst(value);
  return d.isValid() ? d.format("YYYY-MM-DD HH:mm") : fallback;
};

// 2026-10-01 → 2026년 10월 1일(계약서 착공·준공일 · 공사 안내문) — 앞 10자리 날짜만 읽고 시간대를 옮기지 않는다. 못 읽으면 null.
export const formatKoDate = (value: string | null | undefined): string | null => {
  const m = value ? /^(\d{4})-(\d{2})-(\d{2})/.exec(value) : null;
  return m ? `${m[1]}년 ${Number(m[2])}월 ${Number(m[3])}일` : null;
};

/** `<input type="date">` 의 value. ISO 든 날짜 문자열이든 `YYYY-MM-DD` 로. */
export const toDateInput = (value: string | null | undefined): string => {
  if (!value) return "";
  const d = kst(value);
  return d.isValid() ? d.format("YYYY-MM-DD") : "";
};

/** 오늘(KST) 로부터 며칠 뒤의 `YYYY-MM-DD`. 만료일 기본값에 쓴다. */
export const dateAfterDays = (days: number): string =>
  dayjs().tz(APP_TZ).add(days, "day").format("YYYY-MM-DD");

/**
 * 앞자리의 길이. 국번은 **번호마다** 다르다.
 *
 * 앞 3자리로 고정하면 서울 유선이 깨진다 — `02-123-4567` 이 `021-***-4567` 이
 * 되어 **가리는 게 아니라 없는 번호를 보여 준다.** 「누구 건인지 알아보게」 가
 * 목적인데 다른 번호로 읽히면 목적을 잃는다.
 */
const headLength = (digits: string): number => {
  // 서울만 두 자리다.
  if (digits.startsWith("02")) return 2;
  // 050x 안심·평생번호, 0303 인터넷전화는 네 자리가 한 덩이다.
  // 세 자리로 끊으면 `050`·`030` 이라는 **없는 국번**이 나온다 — `021` 과 같은 실수다.
  if (/^(050\d|0303)/.test(digits)) return 4;
  // 010 · 031 · 070 … 나머지 지역·이동통신은 세 자리다.
  if (digits.startsWith("0")) return 3;
  // 1588 · 1644 같은 대표번호. 지역이 없어 앞 네 자리가 한 덩이다.
  return 4;
};

/**
 * 가운데 마스킹. `010-0000-1234` → `010-****-1234`.
 *
 * 목록은 여럿이 같이 보는 화면이라 가운데를 가린다. 앞자리·뒤 4자리는 남겨야
 * "누구 건인지" 를 알아볼 수 있다.
 */
export const maskPhone = (
  phone: string | null | undefined,
  fallback: string = EMPTY_MARK
): string => {
  const raw = (phone ?? "").trim();
  if (!raw) return fallback;

  const digits = raw.replace(/[^\d]/g, "");
  if (digits.length < 7) return raw;

  const head = digits.slice(0, headLength(digits));
  const tail = digits.slice(-4);

  /**
   * 가릴 자리가 없으면 **손대지 않는다.**
   *
   * `1588-1234` 처럼 앞 네 자리 + 뒤 네 자리가 번호 전부인 경우, 별을 끼워 넣으면
   * `1588-***-1234` 가 되어 **한 자리도 안 가린 채 가린 척**한다. 그런 표기는
   * 읽는 사람을 속이고, 대표번호는 어차피 공개 번호다.
   */
  const hidden = digits.length - head.length - 4;
  if (hidden <= 0) return raw;

  // 가운데가 짧아도 최소 세 개는 찍는다 — 별이 한두 개면 자릿수가 드러난다.
  const middle = "*".repeat(Math.max(hidden, 3));
  return `${head}-${middle}-${tail}`;
};
